import React, { useContext, useState } from "react";

import Box from "@material-ui/core/Box";
import DonutLargeIcon from "@material-ui/icons/DonutLarge";
import SubjectIcon from "@material-ui/icons/Subject";
import VerticalKebabIcon from "@material-ui/icons/MoreVert";
import truncate from "lodash/truncate";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";

import { ActionablesContext } from "@contexts/ActionablesContext";
import { Allocations } from "@components/Allocations";
import { Grid } from "@components/layout/Grid";
import { Button } from "@components/Button";
import { Tooltip } from "@components/Tooltip";
import { CardActionButton } from "@components/layout/Card/CardActionButton";
import {
  Card,
  CardContent,
  CardHeading,
  CardFooter,
} from "@components/layout/Card";
import { CueCard, CueCardItem, CueCardTitle } from "@components/CueCard";
import { Hider } from "@components/HOCs/Hider";
import { humanizeString } from "@utils/stringMinipulation";
import { usePopper, Popper } from "@components/Popper";
import useFeatureConfig, { FeatureNames } from "@hooks/useFeatureConfig";
import { useCreateInvestmentProposal } from "@/containers/Scenario/components/CreateInvestmentProposal";

import { useQuickReportAssetIssue } from "../../components/QuickReportAssetIssueModal";
import { AssetsContext } from "../../context";
import { formatValuesForDisplay } from "../../utils";
import { getLongDateFromDate } from "@utils/dateParser";
import { parseNumber } from "@utils/numberConversions";

export const CurrentAssetsCards = () => {
  const { state } = useContext(AssetsContext);
  const {
    assets,
    scenario: { planType },
    tenant: { slug: tenantSlug },
    userDetails: { client_id, profile_id },
    riskProfile,
  } = state;
  const risk_profile_id = riskProfile?.id;
  const [
    { isEnabled: isQuickReportIssuesEnabled },
    { isEnabled: isInvestmentProposalsEnabled },
  ] = useFeatureConfig(
    FeatureNames.CUSTOMER_SUPPORT_TICKET__QUICK_REPORT_ISSUE,
    FeatureNames.INVESTMENT_PROPOSAL__CREATE
  );
  const quickReportIssue = useQuickReportAssetIssue({
    tenantSlug,
    client_id,
    profile_id,
  });
  const createInvestmentProposal = useCreateInvestmentProposal({
    dfmIntegrationSlug: "strategiq",
    tenantSlug,
    client_id,
    profile_id,
    risk_profile_id,
    assets,
    scenarioPlanType: planType,
  });

  return (
    <Grid mediumColumns>
      <AddAssetCard />
      {assets.map((asset, index) => (
        <AssetCard
          key={index}
          asset={asset}
          index={index}
          isQuickReportIssuesEnabled={isQuickReportIssuesEnabled()}
          showQuickReportModalForAsset={
            quickReportIssue.showQuickReportModalForAsset
          }
          isInvestmentProposalsEnabledForAsset={createInvestmentProposal.isEnabledForAsset(
            asset
          )}
          showCreateInvestmentProposalModal={
            createInvestmentProposal.showCreateInvestmentProposalModal
          }
        />
      ))}

      {isQuickReportIssuesEnabled() && quickReportIssue.modal}
      {isInvestmentProposalsEnabled() && createInvestmentProposal.modal}
    </Grid>
  );
};

const AddAssetCard = () => {
  const { readOnly, frozen } = useContext(ActionablesContext);

  if (frozen) return null;

  return (
    <Card darkBorder className="ui-card--add-asset-card text-center">
      {readOnly ? <GoToScenarioPlanning /> : <AddAssetsButtons />}
    </Card>
  );
};

const AddAssetsButtons = () => {
  const {
    state: { scenario },
    actions: { goToAddAsset },
  } = useContext(AssetsContext);

  const scenarioId = scenario?.id;

  return (
    <Box m="1rem">
      <Button
        disabled={!scenarioId}
        fullWidth
        primary
        onClick={goToAddAsset}
        trackAs="Add asset card: Add asset"
      >
        Add asset
      </Button>
      {!scenarioId && <small>Select an individual to add an asset</small>}
    </Box>
  );
};

const GoToScenarioPlanning = () => {
  const {
    state: { onCreateNewScenario, assets },
  } = useContext(AssetsContext);

  const addAssetText = isEmpty(assets)
    ? "Want to add an asset?"
    : "Want to add another asset?";

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mx="2rem"
    >
      <h5>{addAssetText}</h5>
      <Button
        primary
        onClick={() => onCreateNewScenario()}
        trackAs="Add asset card: New scenario"
      >
        Create a new scenario
      </Button>
    </Box>
  );
};

const AssetCard = ({
  index,
  asset,
  isQuickReportIssuesEnabled,
  showQuickReportModalForAsset,
  isInvestmentProposalsEnabledForAsset,
  showCreateInvestmentProposalModal,
}) => {
  const [showAllocations, setShowAllocations] = useState(false);
  return (
    <CueCard isNew={asset.new} warning={asset.flagged_for_deletion}>
      <div>
        <AssetCardHeading
          onClick={() => setShowAllocations(!showAllocations)}
          asset={asset}
          showAllocations={showAllocations}
        />
        <CardContent>
          <AssetName asset={asset} />
          <Hider
            show={!showAllocations}
            render={() => <AssetDetails asset={asset} />}
          />
          <Hider
            show={showAllocations}
            render={() => <AssetAllocations asset={asset} />}
          />
        </CardContent>
      </div>
      <AssetCardFooter
        asset={asset}
        index={index}
        isQuickReportIssuesEnabled={isQuickReportIssuesEnabled}
        showQuickReportModalForAsset={showQuickReportModalForAsset}
        isInvestmentProposalsEnabledForAsset={
          isInvestmentProposalsEnabledForAsset
        }
        showCreateInvestmentProposalModal={showCreateInvestmentProposalModal}
      />
    </CueCard>
  );
};

const AssetDetails = ({ asset }) => {
  const { state } = useContext(AssetsContext);
  const { supportedCurrencies } = state;
  return (
    <>
      <CueCardItem
        name={`Current Value`}
        value={formatValuesForDisplay(
          asset.gross_value,
          asset.currency,
          supportedCurrencies
        )}
      />
      <CueCardItem
        name={`Total Contributions (p/m)`}
        value={formatValuesForDisplay(
          asset.computedAttributes.totalMonthlyContribution,
          asset.currency,
          supportedCurrencies
        )}
      />
      <CueCardItem
        name={`Income (p/m)`}
        value={formatValuesForDisplay(
          asset.computedAttributes.totalMonthlyIncome,
          asset.currency,
          supportedCurrencies
        )}
      />
      {asset.grouping_identifier &&
        (asset.last_automatic_import_at || asset.price_date) && (
          <CueCardItem
            name={`Last Imported`}
            value={getLongDateFromDate(
              asset.last_automatic_import_at || asset.price_date
            )}
          />
        )}
      <CueCardItem
        name={`Last Edit`}
        value={getLongDateFromDate(asset.updated_at)}
      />
      {asset.interest_rate && (
        <CueCardItem
          name={`Return Rate`}
          value={`${(parseNumber(asset.interest_rate) * 100).toFixed(2)}%`}
        />
      )}
      {asset.contract_number && (
        <CueCardItem
          name={`Contract Number`}
          value={<Tooltip title={asset.contract_number}><span>{truncate(asset.contract_number, { length: 24 })}</span></Tooltip>}
        />
      )}
    </>
  );
};

const AssetCardHeading = ({ asset, showAllocations, onClick }) => (
  <CardHeading>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <h5>{humanizeString(asset.asset_type)}</h5>
      <Box display="flex">
        {asset.new && <div className="new-asset">new</div>}
        <ToggleAllocations
          onClick={onClick}
          showAllocations={showAllocations}
        />
      </Box>
    </Box>
  </CardHeading>
);

const AssetName = ({ asset }) => {
  const linkExplanation = asset.linked
    ? "Contract linked: automatic imports can update this asset."
    : "Disconnected: this asset is not linked to automatic imports."

  return (
    <Tooltip title={<><span>{asset.name}</span><br /><span>{linkExplanation}</span></>}>
      <CueCardTitle>
        <span className={asset.linked ? "gci-icon-link" : "gci-icon-unlink"} aria-hidden="true" />
        <span>{truncate(asset.name, { length: 28 })}</span>
      </CueCardTitle>
    </Tooltip>
  )
}

const ToggleAllocations = ({ showAllocations, onClick }) =>
  showAllocations ? (
    <SubjectIcon onClick={onClick} style={{ cursor: "pointer" }} />
  ) : (
    <DonutLargeIcon onClick={onClick} style={{ cursor: "pointer" }} />
  );

const AssetAllocations = ({ asset }) => (
  <>
    <CardContent>
      <Allocations
        assetAllocationsDataset={[assetAllocationsFor(asset)]}
        hideLegend
      />
    </CardContent>
  </>
);

const AssetCardFooter = ({
  asset,
  index,
  isQuickReportIssuesEnabled,
  showQuickReportModalForAsset,
  isInvestmentProposalsEnabledForAsset,
  showCreateInvestmentProposalModal,
}) => {
  const { actions, state } = useContext(AssetsContext);
  const { readOnly } = useContext(ActionablesContext);
  const { scenario } = state;
  const { deleteAsset, unlinkAsset, updateAsset } = actions;
  const popper = usePopper({ uniqueId: asset.id });
  const anyDropdownActionsEnabled =
    isQuickReportIssuesEnabled || isInvestmentProposalsEnabledForAsset;

  if (readOnly || !scenario?.id) {
    return null;
  }

  return (
    <CardFooter>
      <div className="asset-actions">
        {asset.flagged_for_deletion && asset.unlinkModes?.includes("permanent") ? (
          <>
            <CardActionButton
              icon="trash"
              label="Delete asset"
              trackAs={"Disconnected Asset: Delete pending confirmation"}
              className="text-link asset-action flagged-for-deletion"
              onClick={deleteAsset(asset)}
            />
            <Tooltip title="Unlink asset">
              <Button
                aria-label="Unlink asset"
                className="text-link asset-action asset-action--unlink flagged-for-deletion"
                trackAs={"Disconnected Asset: Unlink pending confirmation"}
                onClick={unlinkAsset(asset)}
              >
                <span className="gci-icon-unlink" aria-hidden="true" />
              </Button>
            </Tooltip>
          </>
        ) : (
          <>
            <CardActionButton
              icon="trash"
              label="Delete asset"
              trackAs={"Asset Card: Delete"}
              className="text-link asset-action"
              onClick={deleteAsset(asset)}
            />
            <CardActionButton
              icon="pencil"
              label="Update asset"
              trackAs={"Asset Card: Update"}
              className="text-link asset-action"
              onClick={updateAsset(asset)}
            />
            {(asset.flagged_for_deletion || asset.unlinked_at) && asset.unlinkModes?.includes("permanent") && <Tooltip title="Unlink asset"><Button aria-label="Unlink asset" className="text-link asset-action asset-action--unlink" onClick={unlinkAsset(asset)}><span className="gci-icon-unlink" aria-hidden="true" /></Button></Tooltip>}
          </>
        )}

        {anyDropdownActionsEnabled && (
          <Tooltip title="More asset actions">
            <button
              aria-describedby={popper.id}
              onClick={popper.togglePopper}
              className="asset-actions__dropdown-trigger"
              aria-label="More asset actions"
            >
              <VerticalKebabIcon />
            </button>
          </Tooltip>
        )}
      </div>

      {anyDropdownActionsEnabled && (
        <Popper
          id={popper.id}
          isOpen={popper.isOpen}
          anchorElement={popper.anchorElement}
          closePopper={popper.closePopper}
          variant="card"
        >
          {isQuickReportIssuesEnabled && (
            <Button
              textLink
              className={"asset-actions__dropdown-item"}
              onClick={() => showQuickReportModalForAsset(asset)}
            >
              Report an issue
            </Button>
          )}
          {isInvestmentProposalsEnabledForAsset && (
            <Button
              textLink
              className={"asset-actions__dropdown-item"}
              onClick={() => showCreateInvestmentProposalModal(asset)}
            >
              Create investment proposal
            </Button>
          )}
        </Popper>
      )}
    </CardFooter>
  );
};

const assetAllocationsFor = (asset) =>
  pick(asset, [
    "domestic_equity",
    "domestic_property",
    "domestic_bonds",
    "domestic_cash",
    "offshore_equity",
    "offshore_property",
    "offshore_bonds",
    "offshore_cash",
  ]);
